import React, { Suspense } from 'react';
import { useRoutes } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import Root from './Root';

// All layouts/containers
import DefaultLayout from '../layouts/Default';
import VerticalLayout from '../layouts/Vertical';

import { Loading } from '../bay-metal/helpers/loading/Loading';
import { getUserFromSession } from '../helpers/api/apiCore';
// lazy load all the views

// auth
const Login = React.lazy(() => import('../account/Login'));
const Logout = React.lazy(() => import('../account/Logout'));
const Register = React.lazy(() => import('../account/Register'));
const Confirm = React.lazy(() => import('../account/Confirm'));
const ForgetPassword = React.lazy(() => import('../account/ForgetPassword'));
const LockScreen = React.lazy(() => import('../account/LockScreen'));

//errors
const NoInternet = React.lazy(() => import('../bay-metal/helpers/noInternet/NoInternetPage'));
// -------------------------------------C L I E N T S-----------------------------------------------------------//
const Account = React.lazy(() => import('../bay-metal/pages/acount/Account'));
const Clients = React.lazy(() => import('../bay-metal/pages/trader/Traders'));

// -------------------------------------S E T T I N G S--------------------------------------------------------//

const WareHouse = React.lazy(() => import('../bay-metal/pages/warehouse/WareHouse'));
const CommodityCategory = React.lazy(() => import('../bay-metal/pages/commodity/CommodityCategory'));
const CommodityCategoryManagement = React.lazy(() => import('../bay-metal/pages/commodity/CommodityCategoryManagement'));
const UnitOfMeasure = React.lazy(() => import('../bay-metal/pages/unit-of-measure/UnitOfMeasure'));
const Company = React.lazy(() => import('../bay-metal/pages/freight-provider/FreightProvider'));
const Stock = React.lazy(() => import('../bay-metal/pages/stock/Stock'));
const TagSearch = React.lazy(() => import('../bay-metal/pages/tag-search/TagSearch'));
const MainTransaction = React.lazy(() => import('../bay-metal/pages/transactions/Add/mainTransaction'));

// -------------------------------------T R A N S A C T I O N S-----------------------------------------------//

const Transactions = React.lazy(() => import('../bay-metal/pages/transactions/Transactions'));
const DeletedTransactions = React.lazy(() => import('../bay-metal/pages/deleted-Transaction/DeletedTransaction'));
const Support = React.lazy(() => import('../bay-metal/pages/Help/HelpSupport'));
const EditTransaction = React.lazy(() => import('../bay-metal/pages/transactions/Edit/EditTransactions'));

// -------------------------------------I N V E N T O R Y----------------------------------------------------//
const Ticket = React.lazy(() => import('../bay-metal/pages/Help/TicketsPage'));

// -------------------------------------U S E R----------------------------------------------------//

const User = React.lazy(() => import('../bay-metal/pages/user/CreateUser'));
const CurrentInventoryReport = React.lazy(() => import('../bay-metal/pages/reports/CurrentInventoryReport'));

const loading = () => <Loading />;
const user = getUserFromSession()
const isAdminUser = user?.role === 'admin';
const UnAuthorised = () => (
    <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <h1 className="display-4 text-danger">403</h1>
        <p className="lead">You are not authorised to access this page.</p>
        <a href="/" className="btn btn-primary mt-3">Go Home</a>
    </div>
);
type LoadComponentProps = {
    component: React.LazyExoticComponent<() => JSX.Element>,
};

const LoadComponent = ({ component: Component }: LoadComponentProps) => (
    <Suspense fallback={loading()}>
        <Component />
    </Suspense>
);


const AllRoutes = () => {

    return useRoutes([
        { path: '/', element: <Root /> },
        {
            // public routes
            path: '/',
            element: <DefaultLayout />,
            children: [
                {
                    path: 'account',
                    children: [
                        { path: 'login', element: <LoadComponent component={Login} /> },
                        { path: 'register', element: <LoadComponent component={Register} /> },
                        { path: 'confirm', element: <LoadComponent component={Confirm} /> },
                        { path: 'forget-password', element: <LoadComponent component={ForgetPassword} /> },
                        { path: 'lock-screen', element: <LoadComponent component={LockScreen} /> },
                        { path: 'logout', element: <LoadComponent component={Logout} /> },
                    ],
                },
                {
                    path: 'no-internet',
                    element: <LoadComponent component={NoInternet} />,
                },
            ],
        },
        {
            // auth protected routes
            path: '/',
            element: <PrivateRoute
                // roles={'admin'}
                component={VerticalLayout} />,
            children: [
                {
                    path: 'apps',
                    children: [
                        {
                            path: 'accounts',
                            element: <LoadComponent component={Account} />,
                        },
                        {
                            path: 'traders',
                            element: <LoadComponent component={Clients} />,
                        },
                        {
                            path: 'warehouse',
                            element: <LoadComponent component={WareHouse} />,
                        },
                        {
                            path: 'commodities',
                            element: <LoadComponent component={CommodityCategory} />,
                        },
                        {
                            path: 'unitOfMeasure',
                            element: <LoadComponent component={UnitOfMeasure} />,
                        },
                        {
                            path: 'freight-provider',
                            element: <LoadComponent component={Company} />,
                        },
                        {
                            path: 'tag-search',
                            element: <LoadComponent component={TagSearch} />,
                        },
                        {
                            path: 'stock',
                            element: <LoadComponent component={Stock} />,
                        },
                        {
                            path: 'transactions/:TransactionType',
                            element: <LoadComponent component={MainTransaction} />,
                        },
                        {
                            path: 'inventory-transactions',
                            element: <LoadComponent component={Transactions} />,
                        },
                        {
                            path: 'user',
                            element: <LoadComponent component={User} />,
                        },
                        {
                            path: 'commodity-category',
                            element: <LoadComponent component={CommodityCategoryManagement} />,
                        },
                        {
                            path: 'edit-transactions/:defaultType/:uniqueID/:transactionId',
                            element: <LoadComponent component={EditTransaction} />,
                        },
                        {
                            path: 'help&support',
                            element: <LoadComponent component={Support} />,
                        },
                        {
                            path: 'ticket/:id/:ticketId/:userId',
                            element: <LoadComponent component={Ticket} />,
                        },
                        {
                            path: 'deleted-records',
                            element: <LoadComponent component={isAdminUser ? (DeletedTransactions) : (UnAuthorised)} />,
                        },
                        {
                            path: 'reports/current-inventory',
                            element: <LoadComponent component={CurrentInventoryReport} />,
                        },
                    ],
                },
            ],
        },
    ]);
};

export { AllRoutes };
